import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { ShoppingCartSimple, ArrowLeft, Plus, Trash, Star, Sparkle } from '@phosphor-icons/react';

const EMPTY = { amazon_url: '', title: '', description: '', category: 'power-tools', price_display: '', image_url: '', featured: false };

export default function AdminStore() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [huntQuery, setHuntQuery] = useState('');
  const [hunting, setHunting] = useState(false);
  const [huntResult, setHuntResult] = useState(null);
  const [fetchingImgs, setFetchingImgs] = useState(false);

  const fetchImages = async () => {
    setFetchingImgs(true);
    try {
      const r = await axiosInstance.post('/store/admin/fetch-images', { batch: 10 }, { timeout: 180000 });
      toast({ title: `Updated ${r.data.updated} photos 📷`, description: r.data.note || (r.data.remaining ? `${r.data.remaining} left — click again` : 'All products have real photos now!') });
      load();
    } catch (e) { toast({ title: 'Fetch failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setFetchingImgs(false); }
  };

  const runHunt = async () => {
    setHunting(true);
    setHuntResult(null);
    try {
      const r = await axiosInstance.post('/store/admin/hunt', { query: huntQuery.trim() || null, max_products: 6 }, { timeout: 180000 });
      setHuntResult(r.data);
      toast({ title: `Imported ${r.data.imported} products 🎉`, description: r.data.note || `Search: "${r.data.query}"` });
      load();
    } catch (e) { toast({ title: 'Hunt failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setHunting(false); }
  };

  const load = useCallback(() => {
    axiosInstance.get('/store/products?limit=100').then((r) => {
      setProducts(r.data.products || []);
      setCats(r.data.categories || []);
    }).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const aiDraft = async () => {
    if (!form.amazon_url.includes('amazon.')) { toast({ title: 'Paste an Amazon product link first', variant: 'destructive' }); return; }
    setDrafting(true);
    try {
      const r = await axiosInstance.post('/store/admin/ai-draft', { amazon_url: form.amazon_url }, { timeout: 60000 });
      setForm((f) => ({ ...f, ...r.data.draft }));
      toast({ title: 'Drafted ✨', description: 'Review, add image URL & price, then publish.' });
    } catch (e) { toast({ title: 'Draft failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/store/admin/products', form);
      toast({ title: 'Product live 🎉' });
      setForm(EMPTY);
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Check required fields', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const toggleFeatured = async (p) => {
    await axiosInstance.put(`/store/admin/products/${p.id}`, { featured: !p.featured }).catch(() => {});
    load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await axiosInstance.delete(`/store/admin/products/${p.id}`).catch(() => {});
    load();
  };

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-store-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin')} className="text-white hover:text-[#D95800]" data-testid="store-back-btn"><ArrowLeft size={24} weight="bold" /></button>
          <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center"><ShoppingCartSimple size={26} weight="fill" className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-black text-white uppercase">Affiliate Store</h1>
            <p className="text-neutral-400 text-sm">Curate Amazon picks — your Associates tag is added to every link</p>
          </div>
          <a href="/store" target="_blank" rel="noreferrer" className="ml-auto px-3 py-1.5 bg-white text-sm font-black border-2 border-black" data-testid="view-store-link">View Store →</a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-violet-50 border-2 border-violet-400 shadow-[4px_4px_0px_#7c3aed] p-5 mb-6" data-testid="store-hunter-panel">
          <p className="font-black uppercase mb-1 flex items-center gap-2 text-violet-900"><Sparkle size={18} weight="fill" /> Product Hunter Agent</p>
          <p className="text-xs text-violet-700 mb-3">Searches Amazon for top-rated gear, writes the copy, and imports new products automatically. Also runs by itself every Friday with rotating topics.</p>
          <div className="flex flex-wrap gap-2">
            <input value={huntQuery} onChange={(e) => setHuntQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !hunting && runHunt()}
              placeholder='Optional focus — e.g. "best laser levels" (blank = auto topic)'
              className="flex-1 min-w-[240px] h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="hunt-query-input" />
            <button onClick={runHunt} disabled={hunting}
              className="h-11 px-5 text-sm font-black uppercase bg-violet-600 text-white border-2 border-black disabled:opacity-50" data-testid="hunt-run-btn">
              {hunting ? 'Hunting (~45s)...' : '🔎 Hunt Products'}
            </button>
            <button onClick={fetchImages} disabled={fetchingImgs}
              className="h-11 px-4 text-sm font-black uppercase bg-white text-violet-700 border-2 border-black disabled:opacity-50" data-testid="fetch-images-btn">
              {fetchingImgs ? 'Fetching (~60s)...' : '📷 Fetch Real Photos'}
            </button>
          </div>
          {huntResult && (
            <p className="mt-2 text-sm font-bold text-violet-900" data-testid="hunt-result">
              ✓ {huntResult.imported} imported from "{huntResult.query}"{huntResult.note ? ` — ${huntResult.note}` : ''}
            </p>
          )}
        </div>

        <div className="bg-white border-2 border-black shadow-[4px_4px_0px_#000] p-5 mb-8" data-testid="store-add-form">
          <p className="font-black uppercase mb-3 flex items-center gap-2"><Plus size={18} weight="bold" /> Add a product</p>
          <div className="flex gap-2 mb-3">
            <input value={form.amazon_url} onChange={(e) => setForm({ ...form, amazon_url: e.target.value })}
              placeholder="Paste Amazon product link (https://www.amazon.com/dp/...)" className="flex-1 h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="store-amazon-url" />
            <button onClick={aiDraft} disabled={drafting} className="h-11 px-4 flex items-center gap-1.5 text-sm font-black uppercase bg-violet-600 text-white border-2 border-black disabled:opacity-50" data-testid="store-ai-draft-btn">
              <Sparkle size={15} weight="fill" /> {drafting ? 'Drafting...' : 'AI Fill'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Product title" className="h-11 px-3 border-2 border-black text-sm font-semibold" data-testid="store-title" />
            <div className="flex gap-2">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 px-3 border-2 border-black text-sm font-semibold bg-white flex-1" data-testid="store-category">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <input value={form.price_display} onChange={(e) => setForm({ ...form, price_display: e.target.value })} placeholder="$129.99" className="h-11 px-3 border-2 border-black text-sm font-semibold w-28" data-testid="store-price" />
            </div>
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Product image URL (right-click the Amazon image → Copy image address)" className="h-11 px-3 border-2 border-black text-sm font-semibold md:col-span-2" data-testid="store-image-url" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" rows={2} className="px-3 py-2 border-2 border-black text-sm font-semibold md:col-span-2" data-testid="store-description" />
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-1.5 text-sm font-bold cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="store-featured" /> ★ Pro Pick (featured)
            </label>
            {form.image_url && <img src={form.image_url} alt="preview" className="h-14 border border-neutral-300" />}
            <button onClick={save} disabled={saving} className="ml-auto h-11 px-6 text-sm font-black uppercase bg-emerald-600 text-white border-2 border-black shadow-[3px_3px_0px_#000] disabled:opacity-50" data-testid="store-publish-btn">
              {saving ? 'Publishing...' : '🚀 Publish'}
            </button>
          </div>
        </div>

        <p className="font-black uppercase mb-3">{products.length} products</p>
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} className="bg-white border-2 border-black p-3 flex items-center gap-3" data-testid={`store-row-${p.id}`}>
              <img src={p.image_url} alt="" className="w-14 h-14 object-contain border border-neutral-200 bg-white shrink-0"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/store-placeholder.jpg'; }} />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm truncate">{p.title}</p>
                <p className="text-xs text-neutral-500">{p.category_label} · {p.price_display || 'no price'} · {p.clicks} clicks</p>
              </div>
              <button onClick={() => toggleFeatured(p)} className={`h-9 px-3 text-xs font-bold border-2 ${p.featured ? 'bg-amber-400 border-black' : 'bg-neutral-100 border-neutral-300'}`} data-testid={`store-feature-${p.id}`}>
                <Star size={14} weight={p.featured ? 'fill' : 'bold'} />
              </button>
              <button onClick={() => remove(p)} className="h-9 px-3 text-xs font-bold bg-red-600 text-white border-2 border-black" data-testid={`store-delete-${p.id}`}><Trash size={14} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
